/* import __COLOCATED_TEMPLATE__ from './preview-settings-modal.hbs'; */
/* RESPONSIBLE TEAM: team-actions */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type UserSummary from 'embercom/objects/inbox/user-summary';
import type { PreviewSpoofingSettings } from 'embercom/components/workflows/preview/preview-module';
import type RouterService from '@ember/routing/router-service';
import type IntlService from 'ember-intl/services/intl';
// @ts-expect-error
import { sanitizeHtml } from '@intercom/pulse/lib/sanitize';

interface PreviewSettingsModalArgs {
  isOpen: boolean;
  onClose: () => void;
  onSave: (settings: PreviewSpoofingSettings) => void;
  currentBrandId: string | null;
  currentUser?: UserSummary | null;
}

interface Signature {
  Args: PreviewSettingsModalArgs;
  Element: HTMLDivElement;
}

export default class PreviewSettingsModal extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare router: RouterService;
  @service declare appService: $TSFixMe;

  @tracked brandId: string | null = null;
  @tracked selectedUser: UserSummary | null | undefined = null;
  @tracked shouldReadAndUpdateActualUserData = false;

  constructor(owner: unknown, args: PreviewSettingsModalArgs) {
    super(owner, args);
    this.brandId = args.currentBrandId;
    this.selectedUser = args.currentUser;
  }

  @action
  onSave() {
    this.args.onSave({
      user: this.selectedUser ?? null,
      brandId: this.brandId ?? null,
      shouldCallRealActions: this.shouldCallRealActions,
    });
    this.args.onClose();
  }

  @action
  onCancel() {
    this.brandId = this.args.currentBrandId;
    this.shouldReadAndUpdateActualUserData = false;
    this.args.onClose();
  }

  @action
  handleBrandSelection(brandId: string | null) {
    this.brandId = brandId;
  }

  @action
  handleUserSelection(user: UserSummary | null) {
    this.selectedUser = user;
  }

  get dataConnectorLink() {
    let translatedLink = this.intl.t(
      'operator.custom-bot.editor.preview.preview-settings.data-connector-is-triggered.data-connector-link',
    );
    let url = this.router.urlFor('apps.app.settings.app-settings.custom-actions');
    return sanitizeHtml(
      `<a class="cursor-pointer text-neutral-default font-semibold" href="${url}" target="_blank">${translatedLink}</a>`,
    );
  }

  get shouldCallRealActions(): boolean | null {
    // return null if the feature flag is not enabled
    if (!this.canUseFinPreviewImpersonationWorkflowsAndActions) {
      return null;
    }

    return this.shouldReadAndUpdateActualUserData;
  }

  get canUseFinPreviewImpersonationWorkflowsAndActions() {
    return this.appService.app.canUseFinPreviewImpersonationWorkflowsAndActions;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Workflows::Preview::Modals::PreviewSettingsModal': typeof PreviewSettingsModal;
    'workflows/preview/modals/preview-settings-modal': typeof PreviewSettingsModal;
  }
}
